/**
 * BRE-7698: where a sign-in lands once it completes. The middleware sends a
 * signed-out visitor to `/login?returnTo=<the link they opened>`, and every way
 * out of sign-in goes back there: the emailed code, Google/Microsoft/SSO (via
 * the OAuth `state`), and a visit to /login or /signup that is already signed
 * in. Anyone can put anything in the query string, so each hop re-validates
 * with `sanitizeReturnTo` rather than trusting the hop before it.
 *
 * Imported by the edge middleware and by client components: no Node APIs.
 */

export const RETURN_TO_PARAM = "returnTo";

/** Keeps the value inside URL limits once it rides the OAuth `state` (base64 grows it by a third). */
const MAX_RETURN_TO_LENGTH = 2048;

/** Session routes: landing on one after sign-in signs the user out or loops back to sign-in. */
const DENIED_PATH_PREFIXES = ["/logout", "/login", "/signup", "/auth"];

const PARSE_BASE = "https://return-to.invalid";

function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * The same-origin path + query to land on, or null when `value` is not one.
 * Refuses anything that could leave the origin (`//host`, `/\host`, a scheme)
 * and the session routes above. The input AND the value returned are both
 * checked: resolving dot-segments turns `/.//evil.com` into `//evil.com`,
 * which a browser reads as another host.
 */
export function sanitizeReturnTo(value: unknown): string | null {
  if (typeof value !== "string" || value.length > MAX_RETURN_TO_LENGTH) return null;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  if (hasControlCharacter(value)) return null;

  let url: URL;
  try {
    url = new URL(value, PARSE_BASE);
  } catch {
    return null;
  }
  if (url.origin !== PARSE_BASE) return null;

  let path: string;
  try {
    path = decodeURIComponent(url.pathname).toLowerCase();
  } catch {
    return null;
  }
  if (path.startsWith("//") || path.includes("\\")) return null;
  const denied = DENIED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  if (denied) return null;

  const target = `${url.pathname}${url.search}`;
  return target.length > MAX_RETURN_TO_LENGTH ? null : target;
}

/** `/login` showing `error`, keeping the `returnTo` the bounced request carried so a retry still lands there. */
export function loginErrorPath(error: string, searchParams: URLSearchParams): string {
  const params = new URLSearchParams({ error });
  const target = sanitizeReturnTo(searchParams.get(RETURN_TO_PARAM));
  if (target) params.set(RETURN_TO_PARAM, target);
  return `/login?${params.toString()}`;
}

// authkit-nextjs's handleAuth redirects to the `returnPathname` it decodes from
// `state`, in its own encoding: URL-safe base64 of `{ returnPathname }`, then
// "." and the caller's own state. The dotted form is the one it decodes
// URL-safe; its undotted form runs plain `atob`, which rejects "-" and "_".
const AUTHKIT_STATE_TAG = "brev";

/** The WorkOS authorization `state` that lands the sign-in on `returnTo`, or undefined. */
export function returnToAuthorizationState(returnTo: unknown): string | undefined {
  const target = sanitizeReturnTo(returnTo);
  if (!target) return undefined;
  const internal = btoa(JSON.stringify({ returnPathname: target }))
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
  return `${internal}.${AUTHKIT_STATE_TAG}`;
}

/**
 * The `returnPathname` authkit-nextjs 2.x will read out of a callback `state`,
 * decoded exactly as its `handleState` does, or undefined when it reads none.
 * Unvalidated: the callback checks it with `sanitizeReturnTo`.
 */
export function readAuthkitReturnPathname(state: string | null | undefined): unknown {
  if (!state) return undefined;
  try {
    if (state.includes(".")) {
      const internal = state.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
      return (JSON.parse(atob(internal)) as { returnPathname?: unknown }).returnPathname;
    }
    const decoded = JSON.parse(atob(state)) as { returnPathname?: unknown } | null;
    return decoded?.returnPathname || undefined;
  } catch {
    return undefined;
  }
}
